import { Stack, Alert, Box, Typography } from "@mui/material";
import {
  AnnualProjectCapacity,
  Maybe,
  TotalRevenue,
} from "../../../__generated__/types";
import { HdgTextInput } from "../../../components/hdg-text-input";
import { DropdownSelect } from "../../../components/dropdown-select";
import {
  AnnualProjectCapacityOptions,
  TotalRevenueOptions,
} from "../../../utils";
import { useDesignerApplicationContext } from "../../../hooks/designer-application-context";
import { StaticBusinessGoals } from "./static-business-goals";
import { validateRevenue } from "../validations";
import { useState, useEffect, useCallback } from "react";

export const BusinessGoalsAndMetricsForm = ({
  isEditable = true,
}: {
  isEditable?: boolean;
}) => {
  // Pull the typed updater from context, and read `application` via a safe cast.
  const ctx = useDesignerApplicationContext();
  const { updateApplicationData } = ctx;
  const application = (ctx as any).application ?? {};

  // Local state for form inputs to prevent excessive re-renders
  const [localFormData, setLocalFormData] = useState({
    numOfProjectsLastYear: application?.businessGoals?.overview?.numberOfProjectsLastYear || "",
    totalRevenueLastYear: application?.businessGoals?.overview?.totalRevenueLastYear || "",
    numOfProjectsThisYear: application?.businessGoals?.overview?.numberOfProjectsCurrentYear || "",
    totalRevenueThisYear: application?.businessGoals?.overview?.totalRevenueCurrentYear || "",
    largeScaleProjectCapacity: application?.businessGoals?.projectCapacity?.largeScale || "",
    smallScaleProjectCapacity: application?.businessGoals?.projectCapacity?.smallScale || "",
    currentChallenges: application?.businessGoals?.challenges || "",
    expectations: application?.businessGoals?.expectations || "",
  });

  // Update local state when application data changes
  useEffect(() => {
    
    setLocalFormData({
      numOfProjectsLastYear: application?.businessGoals?.overview?.numberOfProjectsLastYear || "",
      totalRevenueLastYear: application?.businessGoals?.overview?.totalRevenueLastYear || "",
      numOfProjectsThisYear: application?.businessGoals?.overview?.numberOfProjectsCurrentYear || "",
      totalRevenueThisYear: application?.businessGoals?.overview?.totalRevenueCurrentYear || "",
      largeScaleProjectCapacity: application?.businessGoals?.projectCapacity?.largeScale || "",
      smallScaleProjectCapacity: application?.businessGoals?.projectCapacity?.smallScale || "",
      currentChallenges: application?.businessGoals?.challenges || "",
      expectations: application?.businessGoals?.expectations || "",
    });
  }, [application?.businessGoals]);

  // Immediate update function for better UX
  const immediateUpdate = useCallback((field: string, value: any) => {
    let updateData: any = {};
    
    if (field === 'numberOfProjectsLastYear' || field === 'totalRevenueLastYear' || 
        field === 'numberOfProjectsCurrentYear' || field === 'totalRevenueCurrentYear') {
      updateData = {
        businessGoals: {
          ...(application?.businessGoals || {}),
          overview: {
            ...(application?.businessGoals?.overview || {}),
            [field]: value,
          },
        },
      };
    } else if (field === 'largeScale' || field === 'smallScale') {
      updateData = {
        businessGoals: {
          ...(application?.businessGoals || {}),
          projectCapacity: {
            ...(application?.businessGoals?.projectCapacity || {}),
            [field]: value,
          },
        },
      };
    } else if (field === 'challenges' || field === 'expectations') {
      updateData = {
        businessGoals: {
          ...(application?.businessGoals || {}),
          [field]: value,
        },
      };
    }
    
    updateApplicationData(updateData);
  }, [updateApplicationData, application?.businessGoals]);

  const numOfProjectsLastYear = localFormData.numOfProjectsLastYear;
  const totalRevenueLastYear = localFormData.totalRevenueLastYear;
  const numOfProjectsThisYear = localFormData.numOfProjectsThisYear;
  const totalRevenueThisYear = localFormData.totalRevenueThisYear;
  const largeScaleProjectCapacity = localFormData.largeScaleProjectCapacity;
  const smallScaleProjectCapacity = localFormData.smallScaleProjectCapacity;
  const currentChallenges = localFormData.currentChallenges;
  const expectations = localFormData.expectations;

  const isInputValid = (value: string | number | Maybe<number> | undefined) => {
    return value !== "" && value !== undefined && value !== null;
  };

  const validations = {
    numOfProjectsLastYear: !isInputValid(numOfProjectsLastYear)
      ? "Field is Required"
      : false,
    totalRevenueLastYear: !isInputValid(totalRevenueLastYear)
      ? "Field is Required"
      : false,
    numOfProjectsThisYear: !isInputValid(numOfProjectsThisYear)
      ? "Field is Required"
      : false,
    totalRevenueThisYear: !isInputValid(totalRevenueThisYear)
      ? "Field is Required"
      : false,
    largeScaleProjectCapacity: !isInputValid(largeScaleProjectCapacity)
      ? "Field is Required"
      : false,
    smallScaleProjectCapacity: !isInputValid(smallScaleProjectCapacity)
      ? "Field is Required"
      : false,
    currentChallenges: !isInputValid(currentChallenges)
      ? "Field is Required"
      : false,
    expectations: !isInputValid(expectations) ? "Field is Required" : false,
  };

  const setNumOfProjectsLastYear = (value: number) => {
    // Handle both valid numbers and empty values
    if (isNaN(value) || value <= 0) {
      setLocalFormData(prev => ({ ...prev, numOfProjectsLastYear: "" }));
      immediateUpdate('numberOfProjectsLastYear', null);
    } else {
      setLocalFormData(prev => ({ ...prev, numOfProjectsLastYear: value }));
      immediateUpdate('numberOfProjectsLastYear', value);
    }
  };

  const setTotalRevenueLastYear = (value: TotalRevenue) => {
    setLocalFormData(prev => ({ ...prev, totalRevenueLastYear: value }));
    immediateUpdate('totalRevenueLastYear', value);
  };

  const setNumOfProjectsThisYear = (value: number) => {
    // Handle both valid numbers and empty values
    if (isNaN(value) || value <= 0) {
      setLocalFormData(prev => ({ ...prev, numOfProjectsThisYear: "" }));
      immediateUpdate('numberOfProjectsCurrentYear', null);
    } else {
      setLocalFormData(prev => ({ ...prev, numOfProjectsThisYear: value }));
      immediateUpdate('numberOfProjectsCurrentYear', value);
    }
  };

  const setTotalRevenueThisYear = (value: TotalRevenue) => {
    setLocalFormData(prev => ({ ...prev, totalRevenueThisYear: value }));
    immediateUpdate('totalRevenueCurrentYear', value);
  };

  const setLargeScaleProjectCapacity = (value: AnnualProjectCapacity) => {
    setLocalFormData(prev => ({ ...prev, largeScaleProjectCapacity: value }));
    immediateUpdate('largeScale', value);
  };

  const setSmallScaleProjectCapacity = (value: AnnualProjectCapacity) => {
    setLocalFormData(prev => ({ ...prev, smallScaleProjectCapacity: value }));
    immediateUpdate('smallScale', value);
  };

  const setCurrentChallenges = (value: string) => {
    setLocalFormData(prev => ({ ...prev, currentChallenges: value }));
    immediateUpdate('challenges', value);
  };

  const setExpectations = (value: string) => {
    setLocalFormData(prev => ({ ...prev, expectations: value }));
    immediateUpdate('expectations', value);
  };

  if (!isEditable) {
    return (
      <StaticBusinessGoals
        numOfProjectsLastYear={numOfProjectsLastYear as number}
        totalRevenueLastYear={totalRevenueLastYear as TotalRevenue}
        numOfProjectsThisYear={numOfProjectsThisYear as number}
        totalRevenueThisYear={totalRevenueThisYear as TotalRevenue}
        largeScaleProjectCapacity={
          largeScaleProjectCapacity as AnnualProjectCapacity
        }
        smallScaleProjectCapacity={
          smallScaleProjectCapacity as AnnualProjectCapacity
        }
        currentChallenges={currentChallenges}
        expectations={expectations}
      />
    );
  }

  const showValidationText = (validationText: string | boolean) => {
    if (typeof validationText === "string") {
      return validationText;
    }
    return undefined;
  };

  // Validate revenue requirements - use local form data if it's more recent
  const validationData = {
    ...application,
    businessGoals: {
      ...application?.businessGoals,
      overview: {
        ...application?.businessGoals?.overview,
        // Use local form data for current values
        totalRevenueLastYear: localFormData.totalRevenueLastYear || application?.businessGoals?.overview?.totalRevenueLastYear,
        totalRevenueCurrentYear: localFormData.totalRevenueThisYear || application?.businessGoals?.overview?.totalRevenueCurrentYear,
        numberOfProjectsLastYear: localFormData.numOfProjectsLastYear || application?.businessGoals?.overview?.numberOfProjectsLastYear,
        numberOfProjectsCurrentYear: localFormData.numOfProjectsThisYear || application?.businessGoals?.overview?.numberOfProjectsCurrentYear,
      },
      projectCapacity: {
        ...application?.businessGoals?.projectCapacity,
        largeScale: localFormData.largeScaleProjectCapacity || application?.businessGoals?.projectCapacity?.largeScale,
        smallScale: localFormData.smallScaleProjectCapacity || application?.businessGoals?.projectCapacity?.smallScale,
      },
      challenges: localFormData.currentChallenges || application?.businessGoals?.challenges,
      expectations: localFormData.expectations || application?.businessGoals?.expectations,
    }
  };
  

  const revenueValidation = validateRevenue(validationData);

  return (
    <Stack style={{ textAlign: "left" }} direction={"column"} spacing={2}>
      {/* Revenue Validation Info */}
      {!revenueValidation.isValid && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Revenue Requirements:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {revenueValidation.errors.map((error, index) => (
              <li key={index}>
                <Typography variant="body2" style={{ color: '#D97706' }}>{error}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}
      <Stack direction={"column"} spacing={2}>
        <h5 className="font-body font-size-3">Business Overview</h5>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <HdgTextInput<number>
            type="number"
            label={"Number of Projects Last Year"}
            value={numOfProjectsLastYear as number}
            onChange={(value) =>
              setNumOfProjectsLastYear(parseInt(value as unknown as string))
            }
            isValid={isInputValid(numOfProjectsLastYear)}
            validationText={showValidationText(
              validations.numOfProjectsLastYear
            )}
          />
          <DropdownSelect<TotalRevenue>
            onChange={setTotalRevenueLastYear}
            value={totalRevenueLastYear as TotalRevenue}
            options={TotalRevenueOptions}
            label={"Total Revenue Last Year"}
            isValid={isInputValid(totalRevenueLastYear)}
            validationText={showValidationText(
              validations.totalRevenueLastYear
            )}
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <HdgTextInput<number>
            type="number"
            label={"Number of Projects This Year"}
            value={numOfProjectsThisYear as number}
            onChange={(value) =>
              setNumOfProjectsThisYear(parseInt(value as unknown as string))
            }
            isValid={isInputValid(numOfProjectsThisYear)}
            validationText={showValidationText(
              validations.numOfProjectsThisYear
            )}
          />
          <DropdownSelect<TotalRevenue>
            onChange={setTotalRevenueThisYear}
            value={totalRevenueThisYear as TotalRevenue}
            options={TotalRevenueOptions}
            label={"Total Revenue This Year"}
            isValid={isInputValid(totalRevenueThisYear)}
            validationText={showValidationText(
              validations.totalRevenueThisYear
            )}
          />
        </Stack>
      </Stack>

      <Stack direction={"column"} spacing={2}>
        <h5 className="font-body font-size-3">Annual Project Capacity</h5>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <DropdownSelect<AnnualProjectCapacity>
            options={AnnualProjectCapacityOptions}
            value={largeScaleProjectCapacity as any}
            onChange={setLargeScaleProjectCapacity}
            label={"Large Scale, Full Home"}
            isValid={isInputValid(largeScaleProjectCapacity)}
            validationText={showValidationText(
              validations.largeScaleProjectCapacity
            )}
          />
          <DropdownSelect<AnnualProjectCapacity>
            options={AnnualProjectCapacityOptions}
            value={smallScaleProjectCapacity as any}
            onChange={setSmallScaleProjectCapacity}
            label={"Small Project, Room Level"}
            isValid={isInputValid(smallScaleProjectCapacity)}
            validationText={showValidationText(
              validations.smallScaleProjectCapacity
            )}
          />
        </Stack>
      </Stack>

      <Stack direction={"column"} spacing={2}>
        <h5 className="font-body font-size-3">
          Describe your Current Business Challenges.
        </h5>
        <HdgTextInput
          multiline
          label={"Current Challenges"}
          value={currentChallenges}
          onChange={setCurrentChallenges}
          isValid={isInputValid(currentChallenges)}
          validationText={showValidationText(validations.currentChallenges)}
        />
      </Stack>

      <Stack direction={"column"} spacing={2}>
        <h5 className="font-body font-size-3">
          Describe Your Expectations for Signing Up With Home Design Guild.
        </h5>
        <HdgTextInput
          multiline
          label={"Expectations"}
          value={expectations}
          onChange={setExpectations}
          isValid={isInputValid(expectations)}
          validationText={showValidationText(validations.expectations)}
        />
      </Stack>
    </Stack>
  );
};
